import React, { useRef } from 'react';

import {
  ErrorBoundary,
  SearchProvider,
  Results as AppSearchResults,
  WithSearch,
} from '@elastic/react-search-ui';
import { ConfigHelper } from '../../config/ConfigHelper';
import HeApiConnector from '../../connectors/HeApiConnector';
import { ConnectorProvider } from '../../connectors/ConnectorContext';
import DocumentMetaUpdater from '../../components/DocumentMetaUpdater';
import configJson from './heritage-gateway-search-config.json';
import ConditionalPagination from '../../components/ConditionalPagination';
import HeritageGatewaySearchResultView from './HeritageGatewaySearchResultView';
import Layout from '../../components/Layout';
import HelpFooter from '../../components/HelpFooter';
import HeritageGatewaySearchFilters from './HeritageGatewaySearchFilters';
import Results from '../../components/Results';
import ResultHeader from '../../components/ResultHeader';
import NoResults from '../../components/NoResults';
import Header from '../../components/Header';
import FiltersButton from '../../components/FiltersButton';
import ErrorBoundaryContent from '../../components/ErrorBoundaryContent';
import { MapStateProvider } from '../../contexts/MapStateContext';

const configHelper = new ConfigHelper(configJson, 'heritage-gateway');

const connector = new HeApiConnector(
  configHelper.getEndpoints('HERITAGE_GATEWAY'),
);

const config = {
  alwaysSearchOnInitialLoad: true,
  apiConnector: connector,
  initialState: {
    resultsPerPage: configHelper.getPageSizeOptions()[0],
  },
  searchQuery: {
    facets: configHelper.buildFacetConfigFromConfig(),
    ...configHelper.buildSearchOptionsFromConfig(),
    disjunctiveFacets: configHelper.buildDisjunctiveFacetsFromConfig(),
  },
};

export default function HeritageGatewaySearch() {
  const mapStateRef = useRef({
    isMapMode: false,
    zoom: 6,
    bounds: null,
    visibleCount: null,
  });

  return (
    <SearchProvider config={config}>
      <ConnectorProvider connector={connector}>
        <MapStateProvider externalRef={mapStateRef}>
          <DocumentMetaUpdater searchName="Heritage Gateway search" />
          <WithSearch
            mapContextToProps={({
              clearFilters,
              facets,
              filters,
              isLoading,
              wasSearched,
              totalResults,
              resultSearchTerm,
            }) => ({
              clearFilters,
              facets,
              filters,
              isLoading,
              wasSearched,
              totalResults,
              resultSearchTerm,
            })}
          >
            {({
              clearFilters,
              facets,
              filters,
              isLoading,
              wasSearched,
              totalResults,
            }) => {
              return (
                <div className="search-ui" id="main-content">
                  <ErrorBoundary view={ErrorBoundaryContent}>
                    <Layout
                      BodyContent={
                        <AppSearchResults
                          resultView={HeritageGatewaySearchResultView}
                          shouldTrackClickThrough={true}
                          thumbnailField={configHelper.getThumbnailField()}
                          titleField={configHelper.getTitleField()}
                          urlField={configHelper.getUrlField()}
                          view={Results}
                        />
                      }
                      BodyFooter={
                        <>
                          <ConditionalPagination />
                          <HelpFooter />
                        </>
                      }
                      BodyHeader={
                        <ResultHeader
                          configHelper={configHelper}
                          connector={connector}
                          filters={filters}
                          totalResults={totalResults}
                          wasSearched={wasSearched}
                        />
                      }
                      Filters={<HeritageGatewaySearchFilters />}
                      Header={
                        <Header
                          FiltersButton={
                            <FiltersButton
                              SearchFilters={<HeritageGatewaySearchFilters />}
                              clearFilters={clearFilters}
                              facets={facets}
                            />
                          }
                          feedbackUrl="https://www.smartsurvey.co.uk/s/AVAGI4/"
                        />
                      }
                      NoResults={<NoResults />}
                      filters={filters}
                      isLoading={isLoading}
                      searchType="heritage-gateway"
                      totalResults={totalResults}
                      wasSearched={wasSearched}
                    />
                  </ErrorBoundary>
                </div>
              );
            }}
          </WithSearch>
        </MapStateProvider>
      </ConnectorProvider>
    </SearchProvider>
  );
}
